import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuid } from 'uuid';
import { BlockDataForm } from '@plone/volto/components/manage/Form';
import { SidebarPortal } from '@plone/volto/components';
import Schema from './schema';
import MapaView from './View';
import { chave, coordenadas, lerCidade } from './geo';

// "Cidade (UF)" no começo da linha; o resto, depois de vírgula, travessão,
// dois-pontos ou ponto e vírgula, é o detalhe. Marcadores de lista e o ponto
// final saem.
const LINHA =
  /^\s*(?:[-•*–]\s+)?(.+?\s*(?:\(\s*[A-Za-z]{2}\s*\)|[/–-]\s*[A-Za-z]{2}\b))\s*(?:[,;:–—-]\s*(.*?))?\s*[;.]?\s*$/;

/** Lê a lista colada (uma cidade por linha) e devolve os pontos novos. */
export const lerListaColada = (texto, categoria) => {
  const pontos = [];
  const ignoradas = [];
  (texto || '')
    .split(/\n|;\s*(?=[A-ZÀ-Ú])/)
    .map((l) => l.trim())
    .filter(Boolean)
    .forEach((l) => {
      const m = l.match(LINHA);
      const c = m && lerCidade(m[1]);
      if (!c) {
        ignoradas.push(l);
        return;
      }
      const detalhe = (m[2] || '').trim();
      pontos.push({
        '@id': uuid(),
        cidade: `${c.nome} (${c.uf})`,
        detalhe: detalhe ? detalhe[0].toUpperCase() + detalhe.slice(1) : '',
        ...(categoria ? { categoria } : {}),
      });
    });
  return { pontos, ignoradas };
};

const chaveDe = (cidade) => {
  const c = lerCidade(cidade);
  return c ? `${c.uf}:${chave(c.nome)}` : '';
};

/** Põe longitude e latitude nos pontos cuja cidade mudou; diz quais faltam. */
export const completarCoordenadas = (pontos, tabela) => {
  let mudou = false;
  const faltam = [];
  const novos = (pontos || []).map((p) => {
    if (!p || !(p.cidade || '').trim()) return p;
    const k = chaveDe(p.cidade);
    if (p.chave === k && Number.isFinite(p.lon)) return p;
    const lonlat = coordenadas(tabela, p.cidade);
    if (!lonlat) {
      faltam.push(p.cidade);
      if (Number.isFinite(p.lon) || p.chave) {
        mudou = true;
        const { lon, lat, chave: _c, ...resto } = p;
        return resto;
      }
      return p;
    }
    mudou = true;
    return { ...p, lon: lonlat[0], lat: lonlat[1], chave: k };
  });
  return { pontos: novos, mudou, faltam };
};

const precisaDeCoordenadas = (pontos) =>
  (pontos || []).some(
    (p) =>
      p &&
      (p.cidade || '').trim() &&
      (p.chave !== chaveDe(p.cidade) || !Number.isFinite(p.lon)),
  );

/** Edit do Mapa: o desenho do View, os campos na barra lateral e o painel
 *  "Colar a lista de pontos" abaixo do bloco. A tabela das sedes municipais
 *  só é carregada aqui, no editor. */
const MapaEdit = ({ data, onChangeBlock, block, selected }) => {
  const schema = Schema({ data });
  const [colado, setColado] = useState('');
  const [resultado, setResultado] = useState(null);
  const [faltam, setFaltam] = useState([]);

  useEffect(() => {
    if (data.modo === 'estados' || !precisaDeCoordenadas(data.pontos)) {
      setFaltam([]);
      return undefined;
    }
    let vivo = true;
    import('./municipios.json').then((m) => {
      if (!vivo) return;
      const r = completarCoordenadas(data.pontos, m.default || m);
      setFaltam(r.faltam);
      if (r.mudou) onChangeBlock(block, { ...data, pontos: r.pontos });
    });
    return () => {
      vivo = false;
    };
  }, [data, block, onChangeBlock]);

  const ler = () => {
    const primeira = (data.categorias || []).find((c) => c && c['@id']);
    const r = lerListaColada(colado, primeira?.['@id']);
    setResultado({ n: r.pontos.length, ignoradas: r.ignoradas });
    if (r.pontos.length) {
      onChangeBlock(block, { ...data, modo: 'pontos', pontos: r.pontos });
      setColado('');
    }
  };
  // Enter e Esc no painel não sobem ao formulário da página.
  const naoSubir = (e) => e.stopPropagation();

  return (
    <>
      <MapaView data={data} />
      {selected && data.modo !== 'estados' && (
        <div className="ag-mapa__colar" onKeyDown={naoSubir}>
          <label htmlFor={`colar-${block}`}>Colar a lista de pontos</label>
          <p>
            Uma cidade por linha, como &quot;Porto Velho (RO), Base Regional
            Norte&quot;: o que vem depois da cidade vira o detalhe. Colar de
            novo substitui a lista.
          </p>
          <textarea
            id={`colar-${block}`}
            rows={6}
            value={colado}
            onChange={(e) => setColado(e.target.value)}
          />
          <button type="button" onClick={ler} disabled={!colado.trim()}>
            Ler a lista
          </button>
          {resultado && (
            <p className="ag-mapa__colar-resultado" role="status">
              {resultado.n
                ? `${resultado.n} pontos lidos.`
                : 'Nenhuma cidade com UF foi encontrada no texto colado.'}
              {resultado.ignoradas.length > 0 &&
                ` Ficou de fora: ${resultado.ignoradas.join(' / ')}`}
            </p>
          )}
          {faltam.length > 0 && (
            <p className="ag-mapa__colar-resultado" role="status">
              Cidade não encontrada (confira o nome e a UF): {faltam.join(', ')}
            </p>
          )}
        </div>
      )}
      <SidebarPortal selected={selected}>
        <BlockDataForm
          schema={schema}
          title={schema.title}
          onChangeField={(id, value) =>
            onChangeBlock(block, { ...data, [id]: value })
          }
          formData={data}
          block={block}
        />
      </SidebarPortal>
    </>
  );
};

MapaEdit.propTypes = {
  data: PropTypes.object.isRequired,
  onChangeBlock: PropTypes.func.isRequired,
  block: PropTypes.string.isRequired,
  selected: PropTypes.bool,
};

export default MapaEdit;
